/**
 * Closing Disclosure Calculator
 *
 * Financial calculation functions that reconcile with Closing Disclosure line items.
 * All monetary amounts are in cents to maintain precision (e.g., $129,027 = 12,902,700 cents).
 *
 * Functions:
 * - calculateBuyerCashDue: Total cash buyer must bring to closing
 * - calculateSellerProceeds: Net proceeds seller receives after deductions
 * - calculateEscrowTotal: Total escrow funds (insurance + taxes + reserves)
 * - getChargesByCategory: Group charges by type (letter-based)
 * - calculateRoleSummary: Aggregate totals for a specific role
 */

import { NewOrderType } from 'types/order';
import { RoleSummary, PartyRole } from 'types/closing-disclosure';
import { AccountingType } from 'v2-types/order';

/**
 * Extract accounting charges from order structure.
 * Safely handles cases where accounting array is missing or undefined.
 *
 * @param order - The NewOrderType order object
 * @returns Array of accounting entries, or empty array if none exist
 */
function getAccountingEntries(order: NewOrderType | null | undefined) {
  if (!order || !order.accounting) {
    return [];
  }
  return Array.isArray(order.accounting) ? order.accounting : [];
}

/**
 * Calculate total cash buyer must bring to closing.
 *
 * Sums all charges where buyer is the payer at closing time.
 * Only includes amounts in the `at_closing_amount` field for buyer party type.
 *
 * Formula: Sum of all charges where buyer.at_closing_amount > 0
 *
 * @param order - The NewOrderType order object
 * @returns Total cash due in cents (e.g., 12,902,700 for $129,027.00)
 *
 * @example
 * // Returns 12,902,700 (cents) for $129,027.00
 * calculateBuyerCashDue(orderData);
 */
export function calculateBuyerCashDue(order: NewOrderType | null | undefined): number {
  const entries = getAccountingEntries(order);

  let totalCents = 0;

  entries.forEach((entry) => {
    if (!entry.involved_parties || !Array.isArray(entry.involved_parties)) {
      return;
    }

    entry.involved_parties.forEach((party) => {
      // Check if this party is a buyer (by order_kinds or kind field)
      const isOrderKindBuyer = party.order_kinds && party.order_kinds.includes('buyer');
      const isKindBuyer = party.kind === 'buyer';

      if (isOrderKindBuyer || isKindBuyer) {
        // Only include at_closing_amount contributions from buyer
        if (party.at_closing_amount > 0) {
          totalCents += party.at_closing_amount;
        }
      }
    });
  });

  return totalCents;
}

/**
 * Calculate net proceeds seller receives.
 *
 * Formula: purchase_price - commissions - fees + adjustments
 *
 * The seller proceeds calculation aggregates all seller payouts:
 * - Credits to seller (positive amounts where seller is payee)
 * - Debits to seller (negative amounts where seller is payer)
 *
 * This is derived from the accounting entries where seller receives money
 * or pays out funds related to the sale.
 *
 * @param order - The NewOrderType order object
 * @returns Net proceeds in cents (e.g., 33,319,300 for $333,193.00)
 *
 * @example
 * // Returns 33,319,300 (cents) for $333,193.00
 * calculateSellerProceeds(orderData);
 */
export function calculateSellerProceeds(order: NewOrderType | null | undefined): number {
  const entries = getAccountingEntries(order);

  let totalCents = 0;

  entries.forEach((entry) => {
    if (!entry.involved_parties || !Array.isArray(entry.involved_parties)) {
      return;
    }

    entry.involved_parties.forEach((party) => {
      // Check if this party is a seller (by order_kinds or kind field)
      const isOrderKindSeller = party.order_kinds && party.order_kinds.includes('seller');
      const isKindSeller = party.kind === 'seller';

      if (isOrderKindSeller || isKindSeller) {
        // For seller, aggregate both at_closing and before_closing amounts
        // These represent money flowing to the seller (credits) or from seller (debits)
        const sellerAmount = (party.at_closing_amount || 0) + (party.before_closing_amount || 0);
        totalCents += sellerAmount;
      }
    });
  });

  return totalCents;
}

/**
 * Calculate escrow total and breakdown by component.
 *
 * Escrow represents funds held by the title company for property taxes,
 * homeowners insurance, HOA fees, and other reserves.
 *
 * Returns both the total and detailed breakdown by type.
 *
 * @param order - The NewOrderType order object
 * @returns Object with total_cents, insurance_cents, and taxes_cents
 *
 * @example
 * // Returns { total_cents: 418878, insurance_cents: 43302, taxes_cents: 375576 }
 * calculateEscrowTotal(orderData);
 */
export function calculateEscrowTotal(
  order: NewOrderType | null | undefined,
): { total_cents: number; insurance_cents: number; taxes_cents: number } {
  const entries = getAccountingEntries(order);

  let insuranceCents = 0;
  let taxesCents = 0;
  let hoaFeeCents = 0;
  let otherCents = 0;

  entries.forEach((entry) => {
    if (!entry.involved_parties || !Array.isArray(entry.involved_parties)) {
      return;
    }

    // Escrow entries typically have specific letter codes or descriptions
    // Common escrow codes: E (escrow), taxes, insurance, etc.
    const isEscrowEntry =
      entry.letter === 'E' || entry.letter === 'e' || entry.kind === 'escrow';

    if (isEscrowEntry) {
      // Check description to categorize escrow type
      const description = (entry.description || '').toLowerCase();

      if (description.includes('insurance') || description.includes('hazard')) {
        insuranceCents += entry.amount || 0;
      } else if (
        description.includes('tax') ||
        description.includes('property tax') ||
        description.includes('ad valorem')
      ) {
        taxesCents += entry.amount || 0;
      } else if (description.includes('hoa') || description.includes('homeowner association')) {
        hoaFeeCents += entry.amount || 0;
      } else {
        // Uncategorized escrow
        otherCents += entry.amount || 0;
      }
    }
  });

  const totalCents = insuranceCents + taxesCents + hoaFeeCents + otherCents;

  return {
    total_cents: totalCents,
    insurance_cents: insuranceCents,
    taxes_cents: taxesCents,
  };
}

/**
 * Get charges grouped by category (letter-based section).
 *
 * Organizes all accounting entries into groups by their letter code
 * (A=Origination, B=Closing, C=Title, etc. per Closing Disclosure sections).
 *
 * @param order - The NewOrderType order object
 * @returns Record mapping letter codes to arrays of accounting entries
 *
 * @example
 * // Returns { A: [...], B: [...], C: [...], ... }
 * const byCategory = getChargesByCategory(orderData);
 */
export function getChargesByCategory(
  order: NewOrderType | null | undefined,
): Record<string, AccountingType[]> {
  const entries = getAccountingEntries(order);
  const categorized: Record<string, AccountingType[]> = {};

  entries.forEach((entry) => {
    const letter = entry.letter || 'X'; // Default to 'X' for uncategorized
    if (!categorized[letter]) {
      categorized[letter] = [];
    }
    categorized[letter].push(entry);
  });

  return categorized;
}

/**
 * Calculate summary totals for a specific role.
 *
 * Aggregates all amounts associated with a given party role (buyer, seller, lender, etc.)
 * and determines whether they are a net payer or payee in the transaction.
 *
 * @param order - The NewOrderType order object
 * @param roleId - The role identifier (e.g., 'buyer', 'seller', 'lender')
 * @returns Object with total_cents, is_payer, and is_payee flags
 *
 * @example
 * // Returns { total_cents: 12902700, is_payer: true, is_payee: false }
 * calculateRoleSummary(orderData, 'buyer');
 *
 * @example
 * // Returns { total_cents: 33319300, is_payer: false, is_payee: true }
 * calculateRoleSummary(orderData, 'seller');
 */
export function calculateRoleSummary(
  order: NewOrderType | null | undefined,
  roleId: string,
): { total_cents: number; is_payer: boolean; is_payee: boolean } {
  const entries = getAccountingEntries(order);

  let totalCents = 0;
  let totalAtClosing = 0;
  let totalBeforeClosing = 0;

  entries.forEach((entry) => {
    if (!entry.involved_parties || !Array.isArray(entry.involved_parties)) {
      return;
    }

    entry.involved_parties.forEach((party) => {
      // Match role by order_kinds or kind field
      const isOrderKindMatch = party.order_kinds && party.order_kinds.includes(roleId);
      const isKindMatch = party.kind === roleId;

      if (isOrderKindMatch || isKindMatch) {
        const atClosing = party.at_closing_amount || 0;
        const beforeClosing = party.before_closing_amount || 0;
        const roleAmount = atClosing + beforeClosing;

        totalCents += roleAmount;
        totalAtClosing += atClosing;
        totalBeforeClosing += beforeClosing;
      }
    });
  });

  // Determine if this role is a net payer or payee
  // Payer: owes money (negative balance or positive outflows)
  // Payee: receives money (positive balance or positive inflows)
  const isPayer = totalCents < 0;
  const isPayee = totalCents > 0;

  return {
    total_cents: Math.abs(totalCents),
    is_payer: isPayer,
    is_payee: isPayee,
  };
}

export default {
  calculateBuyerCashDue,
  calculateSellerProceeds,
  calculateEscrowTotal,
  getChargesByCategory,
  calculateRoleSummary,
};
